import React from 'react';
import { propertyLandAccessOptions } from '../../constants/property-land-access.constant';
import {
  Controller,
  type Control,
  type UseFormRegister,
} from 'react-hook-form';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useId } from 'src/modules/shared/hooks/useId';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import { PropertyEditorSectionCard } from './property-editor-section-card';
import { PropertyFeatureStateControl } from './property-feature-state-control';
import { PropertyFeatureStateLegend } from './property-feature-state-legend';
import type {
  PropertyFormInputValues,
  PropertyFormValues,
} from '../../validations/property-validations';
import {
  getPropertyFeatureStateLabelKey,
  getSurfaceCoveredHelpKey,
  getSurfaceOutdoorHelpKey,
  getSurfaceTotalHelpKey,
  getSurfaceUsableHelpKey,
  type PropertySurfaceVisibility,
} from '../../utils/property-editor-form.util';

interface PropertyEditorCharacteristicsSectionProps {
  register: UseFormRegister<PropertyFormInputValues>;
  control: Control<PropertyFormInputValues>;
  propertyType?: string | null;
  visibleSurfaceFields: PropertySurfaceVisibility;
  t: I18nContextValue['t'];
  orientationOptions: { content: string; value: string }[];
  featureGroups: {
    title: string;
    features: {
      fieldName: keyof PropertyFormValues;
      label: string;
    }[];
  }[];
}

interface SurfaceFieldProps {
  fieldName: keyof PropertyFormValues;
  label: string;
  helpText: string;
  register: UseFormRegister<PropertyFormInputValues>;
}

interface CharacteristicsSubsectionProps {
  title: string;
  children: React.ReactNode;
}

const SurfaceField: React.FC<SurfaceFieldProps> = ({
  fieldName,
  label,
  helpText,
  register,
}) => (
  <div className="grid content-start grid-rows-[auto_auto_minmax(2.75rem,1fr)] items-start gap-1">
    <Label size="md" className="w-full">
      {label}
    </Label>
    <TextInput
      {...register(fieldName)}
      type="number"
      min="0"
      step="0.01"
      inputMode="decimal"
      placeholder="0"
    />
    <div className="flex min-h-11 w-full items-start pt-1">
      <Text size="tiny" className="text-slate-500">
        {helpText}
      </Text>
    </div>
  </div>
);

const CharacteristicsSubsection: React.FC<CharacteristicsSubsectionProps> = ({
  title,
  children,
}) => (
  <div className="flex flex-col gap-4">
    <div>
      <Text as="h4" size="small" weight="semibold" className="text-slate-900">
        {title}
      </Text>
    </div>
    <div className="flex flex-col gap-4">{children}</div>
  </div>
);

/**
 * Main information and features sections for the property editor.
 */
export const PropertyEditorCharacteristicsSection: React.FC<
  PropertyEditorCharacteristicsSectionProps
> = ({
  register,
  control,
  propertyType,
  visibleSurfaceFields,
  t,
  orientationOptions,
  featureGroups,
}) => {
  const featureLegendId = useId();
  const getFeatureHint = (nextValue: boolean | null) =>
    t('catalog.properties.form.featureState.nextHint', {
      state: t(getPropertyFeatureStateLabelKey(nextValue)),
    });

  return (
    <>
      <PropertyEditorSectionCard
        title={t('catalog.properties.editor.sections.mainInformation')}
        description={t(
          'catalog.properties.editor.sections.mainInformationDescription'
        )}
      >
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <CharacteristicsSubsection
            title={t('catalog.properties.form.subsection.distribution.title')}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label size="md">{t('catalog.properties.form.bedrooms')}</Label>
                <TextInput
                  {...register('bedrooms')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label size="md">
                  {t('catalog.properties.form.bathrooms')}
                </Label>
                <TextInput
                  {...register('bathrooms')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label size="md">{t('catalog.properties.form.rooms')}</Label>
                <TextInput
                  {...register('rooms')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label size="md">
                  {t('catalog.properties.form.parkingLots')}
                </Label>
                <TextInput
                  {...register('parkingLots')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label size="md">
                  {t('catalog.properties.form.warehouses')}
                </Label>
                <TextInput
                  {...register('warehouses')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
            </div>
          </CharacteristicsSubsection>

          <CharacteristicsSubsection
            title={t('catalog.properties.form.subsection.building.title')}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label size="md">{t('catalog.properties.form.floors')}</Label>
                <TextInput
                  {...register('floors')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label size="md">
                  {t('catalog.properties.form.apartmentsPerFloor')}
                </Label>
                <TextInput
                  {...register('apartmentsPerFloor')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label size="md">
                  {t('catalog.properties.form.unitFloor')}
                </Label>
                <TextInput
                  {...register('unitFloor')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label size="md">
                  {t('catalog.properties.form.propertyAge')}
                </Label>
                <TextInput
                  {...register('propertyAge')}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
              {(propertyType === 'LAND' || propertyType === 'PARCEL') && (
                <div className="flex flex-col gap-1 md:col-span-2">
                  <Label size="md">
                    {t('catalog.properties.form.landAccess')}
                  </Label>
                  <Controller
                    control={control}
                    name="landAccess"
                    render={({ field: { value, onChange } }) => (
                      <Select
                        id="property-land-access"
                        aria-label={t('catalog.properties.form.landAccess')}
                        options={[
                          {
                            value: '',
                            content: t(
                              'catalog.properties.form.booleanState.unset'
                            ),
                          },
                          ...propertyLandAccessOptions.map((value) => ({
                            value,
                            content: t(
                              `catalog.properties.form.landAccess.${value}`
                            ),
                          })),
                        ]}
                        value={value ?? ''}
                        onChange={(event) =>
                          onChange(event.target.value || null)
                        }
                      />
                    )}
                  />
                </div>
              )}
              <div className="flex flex-col gap-1 md:col-span-2">
                <Label size="md">
                  {t('catalog.properties.form.orientation')}
                </Label>
                <Controller
                  control={control}
                  name="orientation"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      options={orientationOptions}
                      value={value ?? ''}
                      onChange={(event) => onChange(event.target.value || null)}
                    />
                  )}
                />
              </div>
            </div>
          </CharacteristicsSubsection>
        </div>

        <CharacteristicsSubsection
          title={t('catalog.properties.form.subsection.surfaces.title')}
        >
          <Text size="tiny" className="text-slate-500">
            {t('catalog.properties.form.surfacesHelp')}
          </Text>
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
            {visibleSurfaceFields.showUsable ? (
              <SurfaceField
                fieldName="areaUsable"
                label={t('catalog.properties.form.surfaceUsable')}
                helpText={t(getSurfaceUsableHelpKey(propertyType))}
                register={register}
              />
            ) : null}
            {visibleSurfaceFields.showCovered ? (
              <SurfaceField
                fieldName="areaBuilt"
                label={t('catalog.properties.form.surfaceCovered')}
                helpText={t(getSurfaceCoveredHelpKey(propertyType))}
                register={register}
              />
            ) : null}
            {visibleSurfaceFields.showTotal ? (
              <SurfaceField
                fieldName="areaTotal"
                label={t('catalog.properties.form.surfaceTotal')}
                helpText={t(getSurfaceTotalHelpKey(propertyType))}
                register={register}
              />
            ) : null}
            {visibleSurfaceFields.showOutdoor ? (
              <SurfaceField
                fieldName="areaOutdoor"
                label={t('catalog.properties.form.surfaceOutdoor')}
                helpText={t(getSurfaceOutdoorHelpKey(propertyType))}
                register={register}
              />
            ) : null}
          </div>
        </CharacteristicsSubsection>
      </PropertyEditorSectionCard>

      <PropertyEditorSectionCard
        title={t('catalog.properties.editor.sections.features')}
        description={t(
          'catalog.properties.editor.sections.featuresDescription'
        )}
      >
        <PropertyFeatureStateLegend id={featureLegendId} t={t} />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {featureGroups.map((group) => (
            <CharacteristicsSubsection key={group.title} title={group.title}>
              <div className="-mx-2 grid grid-cols-1 gap-0.5">
                {group.features.map((feature) => (
                  <Controller
                    key={String(feature.fieldName)}
                    control={control}
                    name={feature.fieldName}
                    render={({ field: { value, onChange } }) => (
                      <PropertyFeatureStateControl
                        label={feature.label}
                        value={typeof value === 'boolean' ? value : null}
                        onChange={onChange}
                        getHint={getFeatureHint}
                        legendId={featureLegendId}
                      />
                    )}
                  />
                ))}
              </div>
            </CharacteristicsSubsection>
          ))}
        </div>
      </PropertyEditorSectionCard>
    </>
  );
};
